import { toast } from "@/services/toast";
import { useState } from "react";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import { RiCheckboxCircleFill, RiCloseCircleFill, RiErrorWarningFill } from "react-icons/ri";
import { TiDeleteOutline } from "react-icons/ti";
import { Navigate } from "react-router-dom";

import Modal from "@/components/Modal";
import api from "@/services/api";
import { captureError } from "@/services/error";
import useStore from "@/services/store";

const Account = () => {
  const { user, setUser, setAuth } = useStore();
  const [values, setValues] = useState({
    firstname: user.firstname,
    lastname: user.lastname,
    email: user.email,
  });
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!values.firstname) {
      errors.firstname = "Le prénom est requis";
    }

    setErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const res = await api.put("/user", values);
      if (!res.ok) {
        throw res;
      }
      toast.success("Paramètres mis à jour");
      setUser(res.data);
    } catch (error) {
      captureError(error, { extra: { values } });
    }
  };

  const isChanged = () => values.firstname !== user.firstname || values.lastname !== user.lastname;

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="space-y-12">
      <title>API Engagement - Mon compte</title>
      <h1 className="text-4xl font-bold">Mon compte</h1>

      <form onSubmit={handleSubmit} noValidate className="bg-white p-4 shadow-lg sm:p-12">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-3xl font-bold">Vos informations</h2>
          <button
            type="button"
            className="text-error flex cursor-pointer items-center text-sm"
            onClick={() => {
              api.removeToken();
              setAuth(null, null);
            }}
          >
            <TiDeleteOutline className="mr-2" aria-hidden="true" />
            Se deconnecter
          </button>
        </div>
        <p className="text-text-mention mb-6 text-sm">
          <span className="text-error" aria-hidden="true">
            *
          </span>{" "}
          : champ obligatoire
        </p>
        <div className="grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2">
          <div className="flex flex-col">
            <label className="mb-2 text-sm" htmlFor="firstname">
              Prénom
              <span className="text-error ml-1" aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="firstname"
              className={`input mb-2 ${errors.firstname ? "border-b-error" : "border-b-black"}`}
              name="firstname"
              autoComplete="given-name"
              required
              aria-required="true"
              aria-invalid={errors.firstname ? true : undefined}
              aria-describedby={errors.firstname ? "firstname-error" : undefined}
              value={values.firstname}
              onChange={(e) => setValues({ ...values, firstname: e.target.value })}
            />
            {errors.firstname && (
              <p id="firstname-error" className="text-error flex items-center text-sm" aria-live="polite">
                <RiErrorWarningFill className="mr-2 shrink-0" aria-hidden="true" />
                {errors.firstname}
              </p>
            )}
          </div>

          <div className="flex flex-col">
            <label className="mb-2 text-sm" htmlFor="lastname">
              Nom de famille
            </label>
            <input
              id="lastname"
              className={`input mb-2 ${errors.lastname ? "border-b-error" : "border-b-black"}`}
              name="lastname"
              autoComplete="family-name"
              value={values.lastname}
              onChange={(e) => setValues({ ...values, lastname: e.target.value })}
            />
          </div>
          <div className="flex flex-col sm:col-span-2">
            <label className="mb-2 text-sm" htmlFor="email">
              E-mail
            </label>
            <input id="email" className="input mb-2 border-b-black" name="email" readOnly value={values.email} />
          </div>

          <div className="flex flex-wrap justify-end gap-4 sm:col-span-2">
            <ResetPasswordModal />

            <button type="submit" className="primary-btn" disabled={!isChanged()}>
              Mettre à jour
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const PasswordCriterion = ({ valid, children }) => (
  <div className="flex items-center gap-2">
    {valid ? <RiCheckboxCircleFill className="text-success" aria-hidden="true" /> : <RiCloseCircleFill className="text-text-mention" aria-hidden="true" />}
    <span className={`align-middle text-sm ${valid ? "text-success" : "text-text-mention"}`}>
      {children}
      <span className="sr-only"> : {valid ? "critère validé" : "critère non validé"}</span>
    </span>
  </div>
);

const ResetPasswordModal = () => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async () => {
    const errors = {};
    if (!values.oldPassword) {
      errors.oldPassword = "Le champ Ancien mot de passe est obligatoire";
    }
    if (values.newPassword.length < 12 || !/[a-zA-Z]/.test(values.newPassword) || !/[0-9]/.test(values.newPassword) || !/[!-@#$%^&*(),.?":{}|<>]/.test(values.newPassword)) {
      errors.newPassword = "Le nouveau mot de passe ne respecte pas les critères de sécurité";
    }
    if (values.newPassword === values.oldPassword) {
      errors.newPassword = "Le nouveau mot de passe ne peut pas être identique à l'ancien mot de passe";
    }
    if (values.newPassword !== values.confirmPassword) {
      errors.confirmPassword = "La confirmation du nouveau mot de passe ne correspond pas au nouveau mot de passe";
    }

    setErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const res = await api.put(`/user/change-password`, values);
      if (!res.ok) {
        if (res.code === "INVALID_PASSWORD") {
          setErrors({ newPassword: "Le nouveau mot de passe ne respecte pas les critères de sécurité" });
          return;
        }
        if (res.code === "INVALID_QUERY") {
          setErrors({ oldPassword: "L'ancien mot de passe est incorrect" });
          return;
        }

        throw res;
      }
      toast.success("Mot de passe mis à jour");
      onClose();
    } catch (error) {
      captureError(error);
    }
  };

  const onClose = () => {
    setErrors({});
    setValues({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setOpen(false);
  };

  return (
    <>
      <button type="button" className="secondary-btn" onClick={() => setOpen(true)}>
        Réinitialiser le mot de passe
      </button>

      <Modal open={open} onClose={onClose} title="Changement du mot de passe" className="w-[90vw] max-w-3xl">
        <p className="text-text-mention text-sm">
          <span className="text-error" aria-hidden="true">
            *
          </span>{" "}
          : champ obligatoire
        </p>
        <div className="flex flex-col">
          <label className="mb-2 text-sm" htmlFor="old-password">
            Ancien mot de passe
            <span className="text-error ml-1" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id="old-password"
            className={`input mb-2 ${errors.oldPassword ? "border-b-error" : "border-b-black"}`}
            name="oldPassword"
            type="password"
            autoComplete="current-password"
            required
            aria-required="true"
            aria-invalid={errors.oldPassword ? true : undefined}
            aria-describedby={errors.oldPassword ? "old-password-error" : undefined}
            value={values.oldPassword}
            onChange={(e) => setValues({ ...values, oldPassword: e.target.value })}
          />
          {errors.oldPassword && (
            <p id="old-password-error" className="text-error flex items-center text-sm" aria-live="polite">
              <RiErrorWarningFill className="mr-2 shrink-0" aria-hidden="true" />
              {errors.oldPassword}
            </p>
          )}
        </div>
        <div className="flex flex-col">
          <div className="flex justify-between">
            <label className="mb-2 text-sm" htmlFor="new-password">
              Nouveau mot de passe
              <span className="text-error ml-1" aria-hidden="true">
                *
              </span>
            </label>
            <button
              type="button"
              className="flex cursor-pointer items-center gap-1"
              onClick={() => setShowNewPassword(!showNewPassword)}
              aria-label={showNewPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            >
              {showNewPassword ? <IoMdEyeOff className="text-blue-france" aria-hidden="true" /> : <IoMdEye className="text-blue-france" aria-hidden="true" />}
              <span className="text-blue-france text-xs font-bold">{showNewPassword ? "CACHER" : "AFFICHER"}</span>
            </button>
          </div>
          <input
            id="new-password"
            className={`input mb-2 ${errors.newPassword ? "border-b-error" : "border-b-black"}`}
            name="newPassword"
            type={showNewPassword ? "text" : "password"}
            required
            aria-required="true"
            aria-invalid={errors.newPassword ? true : undefined}
            aria-describedby={errors.newPassword ? "new-password-error new-password-criteria" : "new-password-criteria"}
            value={values.newPassword}
            onChange={(e) => setValues({ ...values, newPassword: e.target.value })}
            autoComplete="new-password"
          />
          {errors.newPassword && (
            <p id="new-password-error" className="text-error flex items-center text-sm" aria-live="polite">
              <RiErrorWarningFill className="mr-2 shrink-0" aria-hidden="true" />
              {errors.newPassword}
            </p>
          )}
        </div>
        <div className="flex flex-col">
          <div className="flex justify-between">
            <label className="mb-2 text-sm" htmlFor="confirm-password">
              Confirmez le nouveau mot de passe
              <span className="text-error ml-1" aria-hidden="true">
                *
              </span>
            </label>
            <button
              type="button"
              className="flex cursor-pointer items-center gap-1"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            >
              {showConfirmPassword ? <IoMdEyeOff className="text-blue-france" aria-hidden="true" /> : <IoMdEye className="text-blue-france" aria-hidden="true" />}
              <span className="text-blue-france text-xs font-bold">{showConfirmPassword ? "CACHER" : "AFFICHER"}</span>
            </button>
          </div>
          <input
            id="confirm-password"
            className={`input mb-2 ${errors.confirmPassword ? "border-b-error" : "border-b-black"}`}
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            aria-required="true"
            aria-invalid={errors.confirmPassword ? true : undefined}
            aria-describedby={errors.confirmPassword ? "confirm-password-error" : undefined}
            value={values.confirmPassword}
            onChange={(e) => setValues({ ...values, confirmPassword: e.target.value })}
          />
          {errors.confirmPassword && (
            <p id="confirm-password-error" className="text-error flex items-center text-sm" aria-live="polite">
              <RiErrorWarningFill className="mr-2 shrink-0" aria-hidden="true" />
              {errors.confirmPassword}
            </p>
          )}
        </div>

        <div id="new-password-criteria" className="flex flex-col gap-2">
          <PasswordCriterion valid={(values.newPassword || "").length >= 12}>Au moins 12 caractères</PasswordCriterion>
          <PasswordCriterion valid={/[a-zA-Z]/.test(values.newPassword)}>Au moins une lettre</PasswordCriterion>
          <PasswordCriterion valid={/[0-9]/.test(values.newPassword)}>Au moins un chiffre</PasswordCriterion>
          <PasswordCriterion valid={/[!-@#$%^&*(),.?":{}|<>]/.test(values.newPassword)}>Au moins un caractère spécial</PasswordCriterion>
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" className="tertiary-btn" onClick={onClose}>
            Annuler
          </button>
          <button type="button" className="primary-btn" onClick={handleSubmit}>
            Mettre à jour
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Account;
